import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  Card,
  TableCell,
  Icon,
  Link,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AppButton from "../buttons/AppButton";
import AppTableHeaderCell from "../pages/AppTableHeaderCell";
import AppTableRowCell from "../pages/AppTableRowCell";
import CustomTypography from "../form/CustomTypography";
import { useState } from "react";
import { observer } from "mobx-react";
import pencil_uderline from "../../../../assets/pencil_uderline.png";
import SingleInput from "../pop-up/SingleInput";
import { InvoiceItem } from "../../../../models/Invoice/Invoice";
import { useStores } from "../../../../use-stores";

interface InvoiceProductsProps {
  items?: InvoiceItem[];
  padding?: string;
  boxShadow?: string;
  updateQuantity?: (id: number, value: number, field: string) => void;
  handleDeleteProduct: (id: number) => void;
  toggleAddProductsPopup: () => void;
  canGiveDiscount?: boolean;
  canUpdateOfferPrice?: boolean;
  canUpdateSellingPrice?: boolean;
  canChangeQuantity?: boolean;
  canDeleteProduct?: boolean;
  canAddProduct?: boolean;
  canSeeTaxes?: boolean;
  canSeeAmount?: boolean;
}
const InvoiceProducts: React.FC<InvoiceProductsProps> = ({
  items,
  padding,
  boxShadow,
  updateQuantity,
  handleDeleteProduct,
  toggleAddProductsPopup,
  canGiveDiscount = true,
  canUpdateOfferPrice = true,
  canUpdateSellingPrice = true,
  canChangeQuantity = true,
  canDeleteProduct = true,
  canAddProduct = true,
  canSeeTaxes = true,
  canSeeAmount = true,
}) => {
  const theme = useTheme();
  const [editableField, setEditableField] = useState<{
    productId: number | null;
    field: string | null;
  }>({
    productId: null,
    field: null,
  });

  const { invoiceStore } = useStores();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalValue, setModalValue] = useState("");

  const handleEditClick = (
    productId: number,
    field: string,
    currentValue: string
  ) => {
    setEditableField({ productId, field });
    setModalValue(currentValue);
    setModalOpen(true);
  };

  const handleModalSubmit = (value: string) => {
    if (!editableField.productId || !editableField.field) return;

    if (editableField.field) {
      updateField(editableField.productId, Number(value), editableField.field);
    }

    setModalOpen(false);
    setEditableField({ productId: null, field: null });
  };

  const updateField = (productId: number, value: number, field: string) => {
    if (updateQuantity) updateQuantity(productId, value, field);
  };

  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
  };

  return (
    <Box sx={{ padding: padding || 3, borderRadius: 2 }}>
      <Card
        sx={{
          p: padding || 5,
          boxShadow:
            boxShadow ||
            "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px;",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
          }}
        >
          <CustomTypography variant="heading4" fontWeight={700}>
            Products Info
          </CustomTypography>
          {canAddProduct && (
            <AppButton
              label="Add Products"
              startIcon={<AddIcon />}
              onClick={() => {
                toggleAddProductsPopup();
              }}
              customStyles={{
                backgroundColor: "transparent",
                padding: 1,
                margin: 0,
                fontSize: "14px",
                color: theme.palette.customColors.pinkDark,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: theme.palette.customColors.pinkDark,
                  boxShadow: "none",
                },
              }}
              disabled={
                Boolean(invoiceStore.invoiceDetails.id) &&
                !Boolean(invoiceStore?.invoiceRequest?.active)
              }
            />
          )}
        </Box>
        {items?.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CustomTypography variant="body1">{""}</CustomTypography>
          </Box>
        )}

        <TableContainer
          sx={{
            borderRadius: "4px",
          }}
        >
          <Table sx={{ tableLayout: "auto", width: "100%" }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor:
                    theme.palette.customColors.grayBackGroundColor,
                  "& th": { borderBottom: "none" },
                }}
              >
                <AppTableHeaderCell
                  title="S.No"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />
                <AppTableHeaderCell
                  title="ID"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                <AppTableHeaderCell
                  title="PRODUCT"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                  }}
                />

                <AppTableHeaderCell
                  title="HSN"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                <AppTableHeaderCell
                  title="ZOHO ID"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                <AppTableHeaderCell
                  title="OFFER PRICE"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                <AppTableHeaderCell
                  title="DISCOUNT"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                <AppTableHeaderCell
                  title="DISCOUNT %"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                <AppTableHeaderCell
                  title="SELLING PRICE"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                <AppTableHeaderCell
                  title="QUANTITY"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                <AppTableHeaderCell
                  title="GST%"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                <AppTableHeaderCell
                  title="TAX"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                <AppTableHeaderCell
                  title="AMOUNT"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                {canDeleteProduct && (
                  <AppTableHeaderCell
                    sx={{
                      color: theme.palette.customColors.tableGrayHeadingColor,
                      position: "sticky",
                      zIndex: 10,
                      minWidth: "80px",
                      right: 0,
                      background:
                        theme.palette.customColors.grayBackGroundColor,
                    }}
                    title=""
                  />
                )}
              </TableRow>
            </TableHead>
            {!items || items.length === 0 ? (
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={12}
                    sx={{
                      borderBottom: "none",
                      padding: 3,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "fit-content",
                        textAlign: "center",
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        fontWeight={700}
                        color="rgba(218, 218, 218, 1)"
                      >
                        NO PRODUCTS FOUND!
                      </CustomTypography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {items?.map((item, index) => (
                  <TableRow key={index}>
                    <AppTableRowCell
                      sx={{
                        fontWeight: 700,
                      }}
                    >
                      {index + 1}.
                    </AppTableRowCell>
                    <AppTableRowCell
                      sx={{
                        fontWeight: 700,
                      }}
                    >
                      <Link
                        href={`/products/add-edit-product?product-id=${item.product_id}`}
                        target="_blank"
                        style={{
                          textDecoration: "underline",
                          color: "black",
                          display: "flex",
                        }}
                      >
                        {item.product_id}
                      </Link>
                    </AppTableRowCell>
                    <AppTableRowCell
                      minWidth="80px"
                      sx={{
                        padding: 1,
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            background: "rgba(242, 242, 242, 1)",
                            padding: 1,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginRight: 2,
                            minHeight: "55px",
                            maxHeight: "55px",
                            borderRadius: "4px",
                          }}
                        >
                          <img
                            style={{
                              maxHeight: "38px",
                              minHeight: "38px",
                              maxWidth: "38px",
                              minWidth: "38px",
                            }}
                            src={item.image_url}
                            alt={item.name}
                          />
                        </Box>
                        <Box sx={{ textAlign: "left" }}>
                          <CustomTypography
                            variant="body2"
                            fontWeight={700}
                            title={item.name}
                            sx={{
                              cursor: "default",
                            }}
                          >
                            {item.name}
                          </CustomTypography>

                          <CustomTypography
                            variant="body3"
                            fontWeight={500}
                            color="rgba(111, 111, 111, 1)"
                          >
                            Category ID: {item.category_id}
                          </CustomTypography>
                        </Box>
                      </Box>
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      {item.hsn_code || "--"}
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      {item.zoho_id || "--"}
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "3px",
                        }}
                      >
                        {item.unit_price}
                        {canUpdateOfferPrice && (
                          <IconButton
                            sx={{
                              width: "10px",
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                              "&:active": {
                                backgroundColor: "transparent",
                              },
                            }}
                            onClick={() =>
                              handleEditClick(
                                item.product_id!,
                                "offerPrice",
                                String(item.unit_price)
                              )
                            }
                          >
                            <Icon sx={{ fontSize: "12px" }}>
                              <img src={pencil_uderline} alt="edit-icon" />
                            </Icon>
                          </IconButton>
                        )}
                      </Box>
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      {item.discount || "--"}
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "3px",
                        }}
                      >
                        {item.discount && item.unit_price
                          ? ((item.discount / item.unit_price) * 100) % 1 === 0
                            ? `${(item.discount / item.unit_price) * 100}`
                            : `${(
                                (item.discount / item.unit_price) *
                                100
                              ).toFixed(2)}`
                          : "0"}
                        {canGiveDiscount && (
                          <IconButton
                            sx={{
                              width: "10px",
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                              "&:active": {
                                backgroundColor: "transparent",
                              },
                            }}
                            onClick={() =>
                              handleEditClick(
                                item.product_id!,
                                "discount%",
                                item.discount && item.unit_price
                                  ? ((item.discount / item.unit_price) * 100) %
                                      1 ===
                                    0
                                    ? `${
                                        (item.discount / item.unit_price) * 100
                                      }`
                                    : `${(
                                        (item.discount / item.unit_price) *
                                        100
                                      ).toFixed(2)}`
                                  : "0"
                              )
                            }
                          >
                            <Icon sx={{ fontSize: "12px" }}>
                              <img src={pencil_uderline} alt="edit-icon" />
                            </Icon>
                          </IconButton>
                        )}
                      </Box>
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "3px",
                        }}
                      >
                        {item.selling_price}
                        {canUpdateSellingPrice && (
                          <IconButton
                            sx={{
                              width: "10px",
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                              "&:active": {
                                backgroundColor: "transparent",
                              },
                            }}
                            onClick={() =>
                              handleEditClick(
                                item.product_id!,
                                "sellingPrice",
                                String(item.selling_price)
                              )
                            }
                          >
                            <Icon sx={{ fontSize: "12px" }}>
                              <img src={pencil_uderline} alt="edit-icon" />
                            </Icon>
                          </IconButton>
                        )}
                      </Box>
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "3px",
                        }}
                      >
                        {item.quantity}
                        {canChangeQuantity && (
                          <IconButton
                            sx={{
                              width: "10px",
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                              "&:active": {
                                backgroundColor: "transparent",
                              },
                            }}
                            onClick={() =>
                              handleEditClick(
                                item.product_id!,
                                "quantity",
                                String(item.quantity)
                              )
                            }
                          >
                            <Icon sx={{ fontSize: "12px" }}>
                              <img src={pencil_uderline} alt="edit-icon" />
                            </Icon>
                          </IconButton>
                        )}
                      </Box>
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      {item.gst_percentage != null
                        ? item.gst_percentage === 0
                          ? "0"
                          : item.gst_percentage
                        : "--"}
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      {canSeeTaxes ? item.taxes?.toFixed(2) : "--"}
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      {canSeeAmount ? item.amount : "--"}
                    </AppTableRowCell>

                    {canDeleteProduct && (
                      <AppTableRowCell
                        minWidth="300px"
                        sx={{
                          padding: "6px",
                          paddingTop: "1rem",
                          paddingBottom: "1rem",
                          textAlign: "center",
                          fontSize: 12,
                          width: "10%",
                          position: "sticky",
                          right: 0,
                          zIndex: 8,
                          background: theme.palette.customColors.white,
                        }}
                      >
                        <IconButton
                          color="secondary"
                          sx={{
                            backgroundColor:
                              theme.palette.customColors.pinkLight,
                            width: 30,
                            height: 30,
                          }}
                          disabled={
                            Boolean(invoiceStore.invoiceDetails.id) &&
                            !Boolean(invoiceStore?.invoiceRequest?.active)
                          }
                          onClick={() => handleDeleteProduct(item.product_id!)}
                        >
                          <DeleteIcon
                            sx={{
                              color: theme.palette.customColors.pinkDark,
                              width: 20,
                              height: 20,
                            }}
                          />
                        </IconButton>
                      </AppTableRowCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <SingleInput
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          heading={`Edit ${
            editableField.field ? formatFieldName(editableField.field) : ""
          }`}
          content={`Enter value`}
          optionalText={`${
            editableField.productId
              ? items?.find(
                  (item) => item.product_id === editableField.productId
                )?.name
              : ""
          }`}
          type="number"
          initialValue={modalValue}
          onSubmit={handleModalSubmit}
        />
      </Card>
    </Box>
  );
};

export default observer(InvoiceProducts);
